import { useEffect } from 'react';

/**
 * Global hotkey wiring for the command palette.
 *
 * Cleanup-safe: the listener is bound exactly once per mount and torn down on
 * unmount. We swallow keystrokes that originate inside an editable target
 * (e.g. the user is typing in a textarea elsewhere on the page) so the cmd-K
 * binding does not fight a textarea's own ⌘K binding.
 */
export function useCommandPaletteHotkey(toggle: () => void): void {
  useEffect(() => {
    function handler(event: KeyboardEvent) {
      const isMod = event.metaKey || event.ctrlKey;
      if (!isMod) return;
      if (event.key !== 'k' && event.key !== 'K') return;

      const target = event.target as HTMLElement | null;
      if (target && isEditableElement(target)) {
        // Allow the binding to still work *inside* our own search input; the
        // CommandPalette's input handles its own keys.
        const inPalette = target.closest('[data-vta-command-root="true"]');
        if (!inPalette) return;
      }

      event.preventDefault();
      toggle();
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);
}

function isEditableElement(el: HTMLElement): boolean {
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}
